/**
 * Reglas de autoevaluatividad de preguntas y quizzes.
 *
 * Módulo hoja a propósito: `lemonade.js` arrastra i18next, el editor de Lemonade y
 * varios cruds, así que importarlo solo para esta regla rompía suites de tests y
 * ataba vistas a dependencias que no necesitan. `lemonade.js` reexporta ambas
 * funciones, de modo que los imports existentes siguen siendo válidos.
 */

type LemonadeQuestionData = {
  data?: {
    isEvaluable?: boolean;
    data?: { isEvaluable?: boolean };
  };
};

type QuestionLike = {
  is_autoevaluative?: number | null;
  data?: string | LemonadeQuestionData | null;
};

/**
 * Autoevaluatividad de UNA pregunta, derivada de su propio lemonade (`data.isEvaluable`).
 * Devuelve 1/0 (no boolean) porque el resto del front usa ese valor tal cual para
 * rellenar el campo `is_autoevaluative` de las preguntas que construye.
 *
 * @param data - lemonade de la pregunta, como string JSON o ya parseado
 * @returns 1 si la pregunta se corrige sola, 0 en caso contrario
 */
export function questionIsAutoevaluative(data?: string | LemonadeQuestionData | null): number {
  try {
    const parsed = typeof data === 'string' ? (JSON.parse(data) as LemonadeQuestionData) : data;
    return parsed?.data?.isEvaluable || parsed?.data?.data?.isEvaluable ? 1 : 0;
  } catch (e) {
    console.error(e);
    return 0;
  }
}

/**
 * Una tarea/quiz es autoevaluativa solo si TODAS sus preguntas lo son.
 * Basta una pregunta de corrección manual para que deje de serlo.
 * Sin preguntas (p.ej. quiz recién creado) se considera autoevaluativa: el campo se
 * habilita por defecto y el backend revalida en el momento de publicar.
 *
 * TAN-9539: no basta con el flag `is_autoevaluative` del backend. En contenidos
 * editoriales llega a 0 en preguntas perfectamente autocorregibles (mcq con
 * `valid_response` y `scoring_type: exactMatch`), así que se acepta también la
 * autoevaluatividad derivada del propio lemonade, que es lo que ya hace el resto
 * del front (parses.js, useAssessmentResultData, AnswerTestView...).
 * La causa de fondo (flag mal en BD) se trata en TAN-9540.
 *
 * @param questions - preguntas del quiz (con flag is_autoevaluative y/o data de lemonade)
 * @returns true si el quiz admite publicación automática de notas
 */
export function getIsLessonItemAutoevaluative(questions?: QuestionLike[] | null): boolean {
  if (!Array.isArray(questions) || questions.length === 0) return true;
  return questions.every((question) => question?.is_autoevaluative === 1 || questionIsAutoevaluative(question?.data) === 1);
}
